import { useState, useEffect, useRef } from "react";
import alffyLogo from "../assets/alffy-logo.png";
import "./LandingV2.css";

// ─── Feature Text Carousel ───────────────────────────────────────────────────
// Messages and speed will be configured later per user request.
const CAROUSEL_MESSAGES = [
  "",
  "Know what you own, and own what you know.",
  "Build your edge with deep fundamental insights",
  "Visualize Everything",
  "",
  "Compare with peers on ratios that matter",
  "Resarch, Summarize and Analyze"
];

const CAROUSEL_INTERVAL_MS = 7000; // easily tunable

function FeatureCarousel({ videoRef }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);
  const fadeRef = useRef(null);

  // Start (or restart) the carousel from index 0
  const startCarousel = useRef(() => { });
  startCarousel.current = () => {
    clearInterval(timerRef.current);
    clearTimeout(fadeRef.current);
    setIsTransitioning(false);
    setActiveIndex(0);

    timerRef.current = setInterval(() => {
      setIsTransitioning(true);
      fadeRef.current = setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % CAROUSEL_MESSAGES.length);
        setIsTransitioning(false);
      }, 400);
    }, CAROUSEL_INTERVAL_MS);
  };

  // Mount: start carousel
  useEffect(() => {
    startCarousel.current();
    return () => {
      clearInterval(timerRef.current);
      clearTimeout(fadeRef.current);
    };
  }, []);

  // Detect video loop: timeupdate fires continuously; a big backward jump = new loop
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    let prevTime = 0;
    const handleTimeUpdate = () => {
      const curr = video.currentTime;
      // If playhead jumped back by more than 1 s, the video looped
      if (curr < prevTime - 1) {
        startCarousel.current();
      }
      prevTime = curr;
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [videoRef]);

  return (
    <div className="lv2-carousel-wrapper">
      {CAROUSEL_MESSAGES.map((msg, i) => (
        <p
          key={i}
          className={`lv2-carousel-text ${i === activeIndex
            ? isTransitioning
              ? "lv2-carousel-text--exit"
              : "lv2-carousel-text--active"
            : "lv2-carousel-text--hidden"
            }`}
        >
          {msg}
        </p>
      ))}
    </div>
  );
}

// ─── Build the direct Google OAuth URL ──────────────────────────────────────
function buildGoogleAuthUrl() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  if (!clientId) return "/login"; // fallback if env var missing

  const redirectUri = encodeURIComponent(window.location.origin + "/login");
  const nonce = Math.random().toString(36).substring(2, 18);

  return (
    `https://accounts.google.com/o/oauth2/v2/auth` +
    `?client_id=${clientId}` +
    `&redirect_uri=${redirectUri}` +
    `&response_type=id_token` +
    `&scope=openid%20email%20profile` +
    `&nonce=${nonce}` +
    `&prompt=select_account`
  );
}

// ─── Main Landing V2 ─────────────────────────────────────────────────────────
export default function LandingV2() {
  const videoRef = useRef(null);

  // Both buttons go directly to Google's sign-in page
  const handleGoogleAuth = () => {
    window.location.href = buildGoogleAuthUrl();
  };

  return (
    <div className="lv2-root">
      {/* ── Top Bar ── */}
      <header className="lv2-topbar">
        {/* Left: Logo + Brand */}
        <div className="lv2-topbar-left">
          <img src={alffyLogo} alt="alffy logo" className="lv2-topbar-logo" />
          <span className="lv2-topbar-brand">alffy</span>
        </div>

        {/* Right: Auth Buttons */}
        <div className="lv2-topbar-right">
          <p className="lv2-topbar-subtext">Explore with us &mdash; no card, no strings!</p>
          <button
            id="lv2-login-btn"
            className="lv2-btn lv2-btn--ghost"
            onClick={handleGoogleAuth}
          >
            Login
          </button>
          <button
            id="lv2-signup-btn"
            className="lv2-btn lv2-btn--primary"
            onClick={handleGoogleAuth}
          >
            Sign Up for Free
          </button>
        </div>
      </header>

      {/* ── Hero: Video fills viewport below top bar ── */}
      <main className="lv2-hero">
        <video
          ref={videoRef}
          className="lv2-video"
          src="/alffy-ss.mp4"
          autoPlay
          loop
          muted
          playsInline
        />

        {/* Subtle dark overlay so carousel text pops */}
        <div className="lv2-overlay" />

        {/* ── Bottom 10%: Feature Carousel ── */}
        <div className="lv2-bottom-strip">
          <FeatureCarousel videoRef={videoRef} />
        </div>
      </main>
    </div>
  );
}
